// src/services/diaryApi.js - Updated with AccessManager authentication pattern

import accessManager from '../Auth/Access'; // Import the AccessManager singleton
import BASE_URL from '../config';

// Meeting API methods - authentication only, no client-side authorization
export const meetingAPI = {
  // Get all meetings with optional filters - requires authentication
  getMeetings: async (filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/diary/meetings/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });

    console.log('[DEBUG] getMeetings response:', response);
    return accessManager.ensureArray(response);
  },
  getGroupMeetings: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
  
    const endpoint = `/diary/meetings/?group_id=${groupId}`;
    const response = await accessManager.request(endpoint, { method: 'GET' });
  
    return response.results || response; // handle paginated & flat
  },

  // Add to meetingAPI object
  getAllMeetingsWithGroups: async (filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/diary/meetings/bulk_meetings/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, { method: 'GET' });
    
    return accessManager.ensureArray(response);
  },
  

  // Get a specific meeting by ID - requires authentication
  getMeeting: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/meetings/${id}/`);
    return response;
  },

  // Create a new meeting - requires authentication
  createMeeting: async (meetingData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/diary/meetings/', {
      method: 'POST',
      body: JSON.stringify(meetingData)
    });
    
    return response;
  },

  // Update a meeting - requires authentication
  updateMeeting: async (id, meetingData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/meetings/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(meetingData)
    });
    
    return response;
  },

  // Delete a meeting - requires authentication
  deleteMeeting: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request(`/diary/meetings/${id}/`, {
      method: 'DELETE'
    });
  },

  // Reschedule a meeting - requires authentication
  rescheduleMeeting: async (id, rescheduleData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/meetings/${id}/reschedule/`, {
      method: 'POST',
      body: JSON.stringify(rescheduleData)
    });
    
    return response;
  },

  // Assign/reassign officers to a meeting only (no date change) -
  // requires authentication. Thin wrapper around reschedule/ so callers
  // (e.g. the Assign Officer dialog) don't need to know it's the same
  // endpoint DetailedInfo.js's reschedule form uses.
  assignOfficers: async (id, officerIds) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/meetings/${id}/reschedule/`, {
      method: 'POST',
      body: JSON.stringify({
        reassign_officers: true,
        officer_ids: officerIds
      })
    });

    return response;
  },

  // Add advance funds to a meeting - requires authentication
  addAdvance: async (id, advanceAmount) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/meetings/${id}/add_advance/`, {
      method: 'POST',
      body: JSON.stringify({ advance_amount: advanceAmount })
    });
    
    return response;
  },

  // End a meeting - requires authentication
  endMeeting: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/meetings/${id}/end_meeting/`, {
      method: 'POST'
    });
    
    return response;
  },

  // Get meeting expenses - requires authentication
  getMeetingExpenses: async (meetingId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/meetings/${meetingId}/expenses/`);
    return response;
  },

  // Add meeting expenses - requires authentication
  addMeetingExpenses: async (meetingId, expenses) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/meetings/${meetingId}/expenses/`, {
      method: 'POST',
      body: JSON.stringify({ expenses })
    });
    
    return response;
  },

  // Add this to your diaryAPI.meetings object
  getMeetingFinancialSummary: async (meetingId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
  
    const response = await accessManager.request(`/diary/meetings/${meetingId}/financial_summary/`);
    return response;
  }

};


// Office Expense API methods
export const officeExpenseAPI = {
  // Get office expenses with filters
  getOfficeExpenses: async (filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/diary/office-expenses/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });
    
    return accessManager.ensureArray(response);
  },

  // Get a specific office expense
  getOfficeExpense: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/office-expenses/${id}/`);
    return response;
  },

  // Create an office expense
  createOfficeExpense: async (expenseData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/diary/office-expenses/', {
      method: 'POST',
      body: JSON.stringify(expenseData)
    });
    
    return response;
  },
  
  getUnifiedExpenses: async (filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/diary/office-expenses/unified_expenses/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });
    
    return response;
  },

  // Update an office expense
  updateOfficeExpense: async (id, expenseData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/office-expenses/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(expenseData)
    });
    
    return response;
  },

  // Delete an office expense
  deleteOfficeExpense: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request(`/diary/office-expenses/${id}/`, {
      method: 'DELETE'
    });
  },

  // Get expense summary
  getExpenseSummary: async (filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/diary/office-expenses/summary/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });
    
    return response;
  }
};

// Expense API methods - authentication only
export const expenseAPI = {
  // Get expenses with optional meeting filter - requires authentication
  getExpenses: async (meetingId = null) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const endpoint = meetingId 
      ? `/diary/expenses/?meeting_id=${meetingId}`
      : '/diary/expenses/';
      
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });
    
    return accessManager.ensureArray(response);
  },

  // Get a specific expense - requires authentication
  getExpense: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/expenses/${id}/`);
    return response;
  },

  // Create an expense - requires authentication
  createExpense: async (expenseData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/diary/expenses/', {
      method: 'POST',
      body: JSON.stringify(expenseData)
    });
    
    return response;
  },

  // Update an expense - requires authentication
  updateExpense: async (id, expenseData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/diary/expenses/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(expenseData)
    });
    
    return response;
  },

  // Delete an expense - requires authentication
  deleteExpense: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request(`/diary/expenses/${id}/`, {
      method: 'DELETE'
    });
  }
};

// Meeting History API methods - authentication only
export const historyAPI = {
  // Get meeting history with optional filters - requires authentication
  getHistory: async (filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    // FIXED: Changed endpoint to match the custom action in views.py
    const endpoint = `/diary/meetings/meeting_history/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });
    
    return accessManager.ensureArray(response);
  },

  // Get specific meeting history - requires authentication
  getHistoryItem: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    // FIXED: Use the meetings endpoint to get ended meetings by ID
    const response = await accessManager.request(`/diary/meetings/${id}/`);
    
    // Verify it's an ended meeting
    if (response.meeting_status !== 'ended') {
      throw new Error('This is not a historical meeting');
    }
    
    return response;
  }
};

// Working Days API methods - authentication only
export const workingDaysAPI = {
  // Get working days configuration - requires authentication
  getConfiguration: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/working-days/api/v1/working-days-config/', {
      method: 'GET'
    });
    
    return accessManager.ensureArray(response);
  },

  // Create working days configuration - requires authentication
  createConfiguration: async (configData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/working-days/api/v1/working-days-config/', {
      method: 'POST',
      body: JSON.stringify({
        name: configData.name || 'Default Working Days Configuration',
        regular_working_days: configData.working_days || configData.regular_working_days,
        description: configData.description || 'Working days configuration'
      })
    });
    
    return response;
  },

  // Update working days configuration - requires authentication
  updateConfiguration: async (id, configData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/working-days/api/v1/working-days-config/${id}/`, {
      method: 'PUT',
      body: JSON.stringify({
        name: configData.name || 'Default Working Days Configuration', 
        regular_working_days: configData.working_days || configData.regular_working_days,
        description: configData.description || 'Working days configuration'
      })
    });
    
    return response;
  },

  // Get the active configuration
  getActiveConfiguration: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/working-days/api/v1/working-days-config/active_config/', {
      method: 'GET'
    });
    
    return response;
  },

  // Load complete configuration (including custom days)
  loadFullConfiguration: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/working-days/api/v1/working-days-config/load_full_config/', {
      method: 'GET'
    });
    
    return response;
  },

  // Save complete configuration (including custom days)
  saveFullConfiguration: async (configData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/working-days/api/v1/working-days-config/save_full_config/', {
      method: 'POST',
      body: JSON.stringify(configData)
    });
    
    return response;
  },

  // Get public holidays - requires authentication
  getPublicHolidays: async (year = null) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const endpoint = year 
      ? `/working-days/api/v1/public-holidays/?year=${year}`
      : '/working-days/api/v1/public-holidays/';

    const response = await accessManager.request(endpoint, {
      method: 'GET'
    });
    
    return accessManager.ensureArray(response);
  },

  // Create public holiday
  createPublicHoliday: async (holidayData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/working-days/api/v1/public-holidays/', {
      method: 'POST',
      body: JSON.stringify(holidayData)
    });
    
    return response;
  },

  // Bulk create public holidays
  bulkCreatePublicHolidays: async (holidays) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/working-days/api/v1/public-holidays/bulk_create_holidays/', {
      method: 'POST',
      body: JSON.stringify({ holidays })
    });
    
    return response;
  },

  // Get custom working days - requires authentication
  getCustomWorkingDays: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/working-days/api/v1/custom-working-days/', {
      method: 'GET'
    });
    
    return accessManager.ensureArray(response);
  },

  // Create custom working day - requires authentication
  createCustomWorkingDay: async (customDayData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    // Transform the data to match backend expectations
    const transformedData = {
      date: customDayData.date,
      day_type: customDayData.is_working_day ? 'working' : 'non_working',
      reason: customDayData.reason,
      is_active: true
    };

    const response = await accessManager.request('/working-days/api/v1/custom-working-days/', {
      method: 'POST',
      body: JSON.stringify(transformedData)
    });
    
    return response;
  },

  // Update custom working day
  updateCustomWorkingDay: async (id, customDayData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const transformedData = {
      date: customDayData.date,
      day_type: customDayData.is_working_day ? 'working' : 'non_working',
      reason: customDayData.reason,
      is_active: customDayData.is_active !== undefined ? customDayData.is_active : true
    };

    const response = await accessManager.request(`/working-days/api/v1/custom-working-days/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(transformedData)
    });
    
    return response;
  },

  // Delete custom working day - requires authentication
  deleteCustomWorkingDay: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request(`/working-days/api/v1/custom-working-days/${id}/`, {
      method: 'DELETE'
    });
  },

  // Bulk delete custom working days
  bulkDeleteCustomWorkingDays: async (ids) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/working-days/api/v1/custom-working-days/bulk_delete/', {
      method: 'DELETE',
      body: JSON.stringify({ ids })
    });
    
    return response;
  }
};

// Utility API methods - authentication only
export const utilityAPI = {
  // Get available officers - requires authentication
  getOfficers: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/diary/utils/officers/', {
      method: 'GET'
    });
    
    return accessManager.ensureArray(response);
  },

  // Get available groups - requires authentication
  getGroups: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/diary/utils/groups/', {
      method: 'GET'
    });
    
    return accessManager.ensureArray(response);
  }
};

// Officer data-access grant API methods - authentication only
// Grants let an officer sync/update a group's member data for a date
// range outside the normal "meeting day ± 1" window, for a limited time.
export const accessGrantAPI = {
  // List grants - filter by group_id, officer_id, meeting_id, active_only
  getGrants: async (filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/diary/access-grants/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await accessManager.request(endpoint, { method: 'GET' });
    return response.results || response;
  },

  // Create a grant - expects { officer_id, group_id, meeting_id?, access_start_date,
  // access_end_date, expires_at, note? }
  createGrant: async (grantData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request('/diary/access-grants/', {
      method: 'POST',
      body: JSON.stringify(grantData)
    });
  },

  // Revoke a grant early
  revokeGrant: async (id) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request(`/diary/access-grants/${id}/revoke/`, {
      method: 'POST'
    });
  }
};

// Combined API object for easy importing
export const diaryAPI = {
  meetings: meetingAPI,
  expenses: expenseAPI,
  history: historyAPI,
  workingDays: workingDaysAPI,
  officeExpenses: officeExpenseAPI, 
  utils: utilityAPI,
  accessGrants: accessGrantAPI
};

// Simplified authorization utility functions for UI components
// These can be used for UI display logic, but backend will enforce actual permissions
export const diaryAuthUtils = {
  // Check if user is authenticated (required for all actions)
  isAuthenticated: () => accessManager.isAuthenticated(),
  
  // Get current user info for UI display
  getCurrentUser: () => accessManager.getCurrentUser(),
  
  // Get user role for UI customization
  getUserRole: () => {
    const user = accessManager.getCurrentUser();
    return user ? user.role : null;
  },
  
  // Get user branch info for UI display
  getUserBranch: () => accessManager.getUserBranch(),
  getUserBranchId: () => accessManager.getUserBranchId(),
  
  // Helper methods for UI role-based display (not for access control)
  isAdmin: () => accessManager.isAdmin(),
  isSecretary: () => accessManager.isSecretary(),
  isUser: () => accessManager.isUser(),
  
  // Generic permission checker - backend will enforce actual permissions
  hasRole: (role) => accessManager.hasRole(role),
  hasAnyRole: (roles) => accessManager.hasAnyRole(roles)
};

// Export the access manager for direct use if needed
export { accessManager };

// Default export
export default diaryAPI;

// Additional helper functions
export const formatters = {
  // Format currency
  formatCurrency: (amount, currency = 'KES') => {
    return `${currency} ${parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  },

  // Format date
  formatDate: (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  },

  // Format datetime
  formatDateTime: (dateTimeString) => {
    return new Date(dateTimeString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  // Get user initials for avatar
  getUserInitials: (firstName, lastName, username) => {
    const name = `${firstName || ''} ${lastName || ''}`.trim() || username;
    return name.split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  },

  // Format date for working days display
  formatWorkingDate: (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }
};

// Constants for frontend use
export const CONSTANTS = {
  SCHEDULED_STATUS: {
    SCHEDULED: 'scheduled',
    UNSCHEDULED: 'unscheduled',
  },
  
  MEETING_STATUS: {
    PENDING: 'pending',
    ONGOING: 'ongoing',
  },
  
  DAYS: [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ],
  
  WEEKS: [
    { value: 'week1', label: 'Week 1' },
    { value: 'week2', label: 'Week 2' },
    { value: 'week3', label: 'Week 3' },
    { value: 'week4', label: 'Week 4' },
  ],
};

// Updated error handler that works with AccessManager
export const handleAPIError = (error) => {
  console.error('API Error:', error);
  
  // Handle authentication errors consistently with AccessManager
  if (error.message.includes('Authentication') || error.message.includes('401')) {
    // AccessManager will handle logout automatically
    accessManager.logout();
    return 'Authentication required. Please log in again.';
  }
  
  // Return user-friendly error message
  return error.message || 'An unexpected error occurred';
};